import type { FC, ReactNode } from "react";

import CopyButton from "@/components/molecules/CopyButton/CopyButton";
import { cn } from "@/lib/utils";

import type { DataListForDetailsType } from "./types";

interface DetailsListProps {
  data: DataListForDetailsType[];
  children?: ReactNode;
  classes?: string;
  columns?: number;
}

const formatDisplayValue = (
  value: DataListForDetailsType["value"]
): ReactNode => {
  if (!value) {
    return "-";
  }
  if (Array.isArray(value)) {
    return value.length > 0 ? value.join(", ") : "-";
  }
  return value;
};

const getCopyValue = ({
  value,
  copyValue,
}: DataListForDetailsType): string | undefined => {
  if (copyValue !== undefined) {
    return copyValue;
  }
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return undefined;
};

const DetailsList: FC<DetailsListProps> = ({
  data,
  children,
  classes,
  columns = 1,
}) => {
  return (
    <ul
      className={cn(
        "gap-[10px]",
        columns > 1
          ? `grid grid-cols-1 sm:grid-cols-${columns}`
          : "flex flex-col",
        classes
      )}
    >
      {data.map((elem, i) => {
        if (elem.isEmpty) {
          return <li key={i}></li>;
        }

        const copyValue = elem.allowCopy ? getCopyValue(elem) : undefined;

        return (
          <li key={elem.title} className={elem.classes}>
            <span className="text-sm text-gray-400 mr-1">{elem.title}:</span>
            <span className="inline-flex items-center gap-1 text-sm break-all">
              <span>{formatDisplayValue(elem.value)}</span>
              {copyValue && (
                <CopyButton
                  textToCopy={copyValue}
                  ariaLabel={`Copy ${elem.title || "value"}`}
                />
              )}
            </span>
          </li>
        );
      })}

      {children}
    </ul>
  );
};

export default DetailsList;
